/**
 * impactSource.js
 *
 * Which impact dataset an LCI input's scores came from.
 *
 * Until now an input carried exactly one score block — `traci_scores_per_unit`
 * — with nothing saying where it came from. That is fine while ecoinvent is the
 * only source, but GREET-derived factors would either overwrite it or force a
 * parallel set of templates. So an input may now carry several, keyed by source:
 *
 *   {
 *     name: 'Electricity, high voltage',
 *     quantity: 5, unit: 'kilowatt hour',
 *
 *     // The original, unqualified block. Still the shape every existing
 *     // template uses, and still where the active source's scores are read
 *     // from when no better match exists.
 *     impact_source: 'ecoinvent_3.11',        // optional; defaults to ecoinvent
 *     traci_scores_per_unit: { climate_change: 0.42, ... },
 *     activity_name: '...', reference_product: '...', location: 'US',
 *
 *     // Additional datasets, same inner shape.
 *     impact_sources: {
 *       greet_2025: {
 *         traci_scores_per_unit: { climate_change: 0.39, ... },
 *         activity_name: '...', location: 'US',
 *       },
 *     },
 *   }
 *
 * The project picks the active one via `project.impact_dataset`. Everything
 * that reads scores goes through `getImpactScores` / `hasImpactScores` so that
 * adding a dataset is a data change, not a code change.
 *
 * Back-compatibility: a template with no `impact_source` and no
 * `impact_sources` resolves under the default dataset, which is also the
 * default active one — so existing projects behave exactly as before.
 */

'use strict';

export const ECOINVENT_SOURCE = 'ecoinvent_3.11';
export const GREET_SOURCE = 'greet_2025';

/** The source an unqualified `traci_scores_per_unit` block is assumed to be. */
export const DEFAULT_IMPACT_SOURCE = ECOINVENT_SOURCE;

export const IMPACT_SOURCE_LABELS = {
  [ECOINVENT_SOURCE]: 'ecoinvent 3.11',
  [GREET_SOURCE]: 'GREET 2025',
};

/** Human-readable name for a source key, falling back to the key itself. */
export function impactSourceLabel(source) {
  return IMPACT_SOURCE_LABELS[source] || source || DEFAULT_IMPACT_SOURCE;
}

/** Citation for each dataset and for the method, as the sources popups show them. */
export const IMPACT_SOURCE_CITATIONS = {
  [GREET_SOURCE]: { cite: 'Argonne National Laboratory. (2025). R&D GREET 2025 [Model].', url: 'https://greet.anl.gov/' },
  [ECOINVENT_SOURCE]: {
    cite: 'ecoinvent. (2024). ecoinvent database, version 3.11.',
    url: 'https://support.ecoinvent.org/ecoinvent-version-3.11',
  },
};
export const TRACI_CITATION = {
  cite: 'Bare, J. (2012). TRACI version 2.1 user’s guide (EPA/600/R-12/554). US Environmental Protection Agency.',
  url: 'https://www.epa.gov/chemical-research/tool-reduction-and-assessment-chemicals-and-other-environmental-impacts-traci',
};

/**
 * The order the UI offers datasets in wherever the user picks one: open data
 * first. ecoinvent stays the default for the built-in templates, whose inputs
 * carry exact ecoinvent scores, but a lookup against it returns noised values.
 */
export const IMPACT_SOURCE_ORDER = [GREET_SOURCE, ECOINVENT_SOURCE];

/** Label for a dataset in a picker: says what a lookup against it is worth. */
export function impactSourceChoiceLabel(source) {
  if (source === GREET_SOURCE) return `${impactSourceLabel(source)} (exact, open data)`;
  if (source === ECOINVENT_SOURCE) return `${impactSourceLabel(source)} (illustrative)`;
  return impactSourceLabel(source);
}

/** One sentence on what a lookup against the dataset returns. */
export function impactSourceLookupNote(source) {
  if (source === GREET_SOURCE) {
    return 'Public data from Argonne, used exactly. It covers fewer materials than ecoinvent: an input with no GREET match stays unlinked and counts as zero.';
  }
  if (source === ECOINVENT_SOURCE) {
    return 'ecoinvent is licensed, so lookup values here carry ±50% noise and are illustrative only. The built-in template inputs are exact. For exact scores, switch to GREET 2025 open data.';
  }
  return '';
}

/**
 * Categories a dataset cannot score. GREET records only 11 air pollutants, with
 * no toxics and no ozone-depleting substances, so these always come out 0. That
 * 0 means "not covered", not "no impact", and must be shown that way.
 * (Eutrophication is covered, but only through NOx to air.)
 */
export const NOT_COVERED_CATEGORIES = {
  [GREET_SOURCE]: [
    'ecotoxicity_freshwater',
    'human_toxicity_carcinogenic',
    'human_toxicity_non_carcinogenic',
    'ozone_depletion',
  ],
};

/** Whether `source` can score TRACI category `key` at all. */
export function isCategoryCovered(source, key) {
  return !(NOT_COVERED_CATEGORIES[source] || []).includes(key);
}

// ---------------------------------------------------------------------------
// Score key spelling
// ---------------------------------------------------------------------------

/**
 * The database lookups (`search-traci-lookup`, in both the web shim and the
 * Electron main process) name three TRACI categories differently from the
 * calculator and from every shipped template:
 *
 *   lookup                              calculator / templates
 *   human_toxicity_non-carcinogenic  →  human_toxicity_non_carcinogenic
 *   particulate_matter_formation     →  particulate_matter
 *   photochemical_oxidant_formation  →  photochemical_oxidant
 *
 * The calculator reads `scores[key] || 0`, so a mismatched key is not an error
 * — it is a silent zero. Any input a user linked themselves contributed nothing
 * to those three categories. Both lookups now emit the canonical spelling;
 * normalizing on read as well repairs inputs that were linked and saved before
 * the fix, without needing a migration pass over stored projects.
 */
const SCORE_KEY_ALIASES = {
  'human_toxicity_non-carcinogenic': 'human_toxicity_non_carcinogenic',
  particulate_matter_formation: 'particulate_matter',
  photochemical_oxidant_formation: 'photochemical_oxidant',
};

/** Score block with legacy category spellings renamed to the canonical ones. */
export function normalizeScoreKeys(scores) {
  if (!scores) return scores;

  const aliased = Object.keys(scores).filter(k => k in SCORE_KEY_ALIASES);
  if (aliased.length === 0) return scores;

  const out = { ...scores };
  for (const legacy of aliased) {
    const canonical = SCORE_KEY_ALIASES[legacy];
    // A canonical key already present wins — never clobber a good value.
    if (out[canonical] === undefined) out[canonical] = out[legacy];
    delete out[legacy];
  }
  return out;
}

// ---------------------------------------------------------------------------
// Project-level selection
// ---------------------------------------------------------------------------

/** The dataset a project's results should be computed from. */
export function getActiveImpactSource(project) {
  return project?.impact_dataset || DEFAULT_IMPACT_SOURCE;
}

/** Project with a different active dataset. Does not touch the inputs. */
export function setActiveImpactSource(project, source) {
  return { ...project, impact_dataset: source };
}

// ---------------------------------------------------------------------------
// Input-level resolution
// ---------------------------------------------------------------------------

function isScoreBlock(scores) {
  return !!scores && typeof scores === 'object' && Object.keys(scores).length > 0;
}

/** The source key an input's unqualified score block belongs to. */
export function getBaseImpactSource(input) {
  return input?.impact_source || DEFAULT_IMPACT_SOURCE;
}

/**
 * Resolve an input's link for one dataset.
 * Returns { source, scores, activity_name, reference_product, location, database }
 * or null when the input is not linked under that dataset.
 *
 * `source` defaults to the project's active dataset when omitted.
 */
export function getImpactLink(input, source = DEFAULT_IMPACT_SOURCE) {
  if (!input) return null;

  const qualified = input.impact_sources?.[source];
  if (qualified && isScoreBlock(qualified.traci_scores_per_unit)) {
    return {
      source,
      scores: normalizeScoreKeys(qualified.traci_scores_per_unit),
      activity_name: qualified.activity_name || input.activity_name || input.name || '',
      reference_product: qualified.reference_product || '',
      location: qualified.location || '',
      database: qualified.database || source,
      note: qualified.note || '',
    };
  }

  if (getBaseImpactSource(input) === source && isScoreBlock(input.traci_scores_per_unit)) {
    return {
      source,
      scores: normalizeScoreKeys(input.traci_scores_per_unit),
      activity_name: input.activity_name || input.name || '',
      reference_product: input.reference_product || '',
      location: input.location || '',
      database: input.database || source,
    };
  }

  return null;
}

/**
 * Per-unit scores for one dataset, or an empty object when unlinked.
 * Empty rather than null so callers can index it without guarding.
 */
export function getImpactScores(input, source = DEFAULT_IMPACT_SOURCE) {
  return getImpactLink(input, source)?.scores || {};
}

/**
 * A price-only row (a "Cost-Only Item", or a part BatPaC prices whole): it has
 * no impacts by design, so it never counts as unlinked.
 */
export function isCostOnly(input) {
  return Boolean(input?.cost_type);
}

/** Whether an input is linked under a dataset. */
export function hasImpactScores(input, source = DEFAULT_IMPACT_SOURCE) {
  return getImpactLink(input, source) !== null;
}

/** Every dataset this input carries scores for, base source first. */
export function listImpactSources(input) {
  const sources = [];
  if (isScoreBlock(input?.traci_scores_per_unit)) sources.push(getBaseImpactSource(input));
  for (const [key, block] of Object.entries(input?.impact_sources || {})) {
    if (isScoreBlock(block?.traci_scores_per_unit) && !sources.includes(key)) sources.push(key);
  }
  return sources;
}

/** Every dataset any input in the project carries scores for. */
export function listProjectImpactSources(project) {
  const seen = new Set();
  for (const proc of Object.values(project?.processes || {})) {
    for (const input of proc?.lci_inputs || []) {
      listImpactSources(input).forEach(s => seen.add(s));
    }
  }
  if (seen.size === 0) seen.add(getActiveImpactSource(project));
  return [...seen];
}

/**
 * Attach a dataset's scores to an input, returning a new input.
 *
 * The first source written lands in the unqualified block (tagged with
 * `impact_source`) so single-source templates keep the flat shape they have
 * today; later sources go under `impact_sources`.
 */
export function setImpactLink(input, source, link) {
  const { scores: rawScores, activity_name, reference_product, location, database } = link || {};
  if (!isScoreBlock(rawScores)) return input;
  const scores = normalizeScoreKeys(rawScores);

  const baseSource = getBaseImpactSource(input);
  const baseOccupied = isScoreBlock(input?.traci_scores_per_unit);

  if (!baseOccupied || baseSource === source) {
    return {
      ...input,
      impact_source: source,
      traci_scores_per_unit: { ...scores },
      ...(activity_name !== undefined ? { activity_name } : {}),
      ...(reference_product !== undefined ? { reference_product } : {}),
      ...(location !== undefined ? { location } : {}),
      ...(database !== undefined ? { database } : {}),
    };
  }

  return {
    ...input,
    impact_sources: {
      ...(input.impact_sources || {}),
      [source]: {
        traci_scores_per_unit: { ...scores },
        ...(activity_name !== undefined ? { activity_name } : {}),
        ...(reference_product !== undefined ? { reference_product } : {}),
        ...(location !== undefined ? { location } : {}),
        ...(database !== undefined ? { database } : {}),
      },
    },
  };
}

/** Drop one dataset's scores from an input. */
export function removeImpactLink(input, source) {
  if (!input) return input;

  if (input.impact_sources?.[source]) {
    const remaining = { ...input.impact_sources };
    delete remaining[source];
    const next = { ...input };
    if (Object.keys(remaining).length) next.impact_sources = remaining;
    else delete next.impact_sources;
    return next;
  }

  if (getBaseImpactSource(input) === source) {
    const next = { ...input };
    delete next.traci_scores_per_unit;
    delete next.impact_source;
    return next;
  }

  return input;
}

/**
 * How many inputs are linked / unlinked under a dataset.
 * Used to warn when switching datasets would leave gaps.
 *
 * `unlinkedCarbonShare` sizes the gap: the share of the carbon total the
 * unlinked inputs would add, going by another dataset's scores for them
 * (`estimatedWith`). It is for the coverage note only; those scores are never
 * added to a result. Null when nothing with a quantity is unlinked, or when an
 * unlinked input has no other dataset's scores to size it with.
 */
export function impactSourceCoverage(project, source = DEFAULT_IMPACT_SOURCE) {
  let linked = 0;
  let total = 0;
  const unlinked = [];
  let linkedCarbon = 0;
  let unlinkedCarbon = 0;
  let sizable = true;
  let estimatedWith = null;

  for (const proc of Object.values(project?.processes || {})) {
    for (const input of proc?.lci_inputs || []) {
      if (isCostOnly(input)) continue;
      total += 1;
      const qty = Number(input.base_quantity ?? input.quantity) || 0;
      if (hasImpactScores(input, source)) {
        linked += 1;
        linkedCarbon += qty * (Number(getImpactScores(input, source).climate_change) || 0);
        continue;
      }
      unlinked.push(input.name);
      if (qty === 0) continue;
      const others = listImpactSources(input).filter(s => s !== source);
      const other = others.includes(ECOINVENT_SOURCE) ? ECOINVENT_SOURCE : others[0];
      if (!other) { sizable = false; continue; }
      estimatedWith = estimatedWith || other;
      unlinkedCarbon += qty * (Number(getImpactScores(input, other).climate_change) || 0);
    }
  }

  const whole = linkedCarbon + unlinkedCarbon;
  const unlinkedCarbonShare = sizable && unlinkedCarbon > 0 && whole > 0 ? unlinkedCarbon / whole : null;
  return {
    source, linked, total, unlinked,
    unlinkedCarbonShare,
    estimatedWith: unlinkedCarbonShare === null ? null : estimatedWith,
  };
}

/** "0.4%", "3%", "under 0.1%": a coverage share for a sentence. */
export function formatCoverageShare(share) {
  const pct = share * 100;
  if (pct < 0.1) return 'under 0.1%';
  return `${pct < 10 ? pct.toFixed(1).replace(/\.0$/, '') : Math.round(pct)}%`;
}
